import { useTranslation } from "react-i18next";
import type { CompetitorKpiCards as CompetitorKpiCardsData, CompetitorKpiMetric } from "@nexus/contracts";
import { CompetitorKpiCard } from "../CompetitorKpiCard/CompetitorKpiCard";

/** `blocked`: the applied filters lack a brand or competitor, so nothing can load yet. */
export type CompetitorKpiCardsState = "loading" | "empty" | "ready" | "error" | "blocked";

interface CompetitorKpiCardsProps {
  state: CompetitorKpiCardsState;
  data?: CompetitorKpiCardsData;
  errorMessage?: string;
  /** Applied filter key — replays count-up animation on cache hits. */
  animationKey?: string;
  selectedMetric?: CompetitorKpiMetric | null;
  onCardClick?: (metric: CompetitorKpiMetric) => void;
  /** Hovered or focused card, or null when the pointer/focus leaves it. */
  onCardHover?: (metric: CompetitorKpiMetric | null) => void;
  youLogoUrl?: string | null;
  competitorLogoUrl?: string | null;
}

const CARD_ORDER: (keyof CompetitorKpiCardsData)[] = [
  "activeAds",
  "adsLaunched",
  "netNewCreatives",
  "newHooks",
  "creativeLifespan",
  "landingPages",
];

export function CompetitorKpiCards({
  state,
  data,
  errorMessage,
  animationKey,
  selectedMetric,
  onCardClick,
  onCardHover,
  youLogoUrl,
  competitorLogoUrl,
}: CompetitorKpiCardsProps) {
  const { t } = useTranslation();

  if (state === "blocked") {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl border bg-card px-4 text-sm text-muted-foreground">
        {t(
          "dashboard.kpiCards.selectionRequired",
          "Select your brand and main competitor to see data.",
        )}
      </div>
    );
  }

  if (state === "empty") {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl border bg-card px-4 text-sm text-muted-foreground">
        {t("dashboard.kpiCards.empty")}
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl border border-destructive/40 bg-destructive/5 px-4 text-sm text-destructive">
        {errorMessage ?? t("dashboard.kpiCards.error")}
      </div>
    );
  }

  const isLoading = state === "loading";
  const isClickable = state === "ready" && !!onCardClick;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {CARD_ORDER.map((key, i) => {
        const card = data?.[key];
        return (
          <CompetitorKpiCard
            key={key}
            isLoading={isLoading}
            skeletonDelayMs={i * 160}
            animationKey={animationKey}
            title={card?.label ?? ""}
            you={card?.you ?? 0}
            competitor={card?.mainCompetitor ?? 0}
            benchmark={card?.benchmark ?? 0}
            youLabel={card?.brandLabels.you}
            competitorLabel={card?.brandLabels.mainCompetitor}
            youLogoUrl={youLogoUrl}
            competitorLogoUrl={competitorLogoUrl}
            isClickable={isClickable}
            isSelected={selectedMetric === key}
            onClick={isClickable ? () => onCardClick?.(key) : undefined}
            onHoverChange={
              isClickable && onCardHover
                ? (isHovered) => onCardHover(isHovered ? key : null)
                : undefined
            }
          />
        );
      })}
    </div>
  );
}
